import * as LocalStorage from '../../utility/local.storage';
import {
  CLOSING_SESSION_RECORD_TTL_MS,
  FINISHED_SESSION_RECORD_TTL_MS,
  SESSION_RECORD_LIMIT,
  SESSION_RECORD_PREFIX,
  isSessionTerminationRecord,
  property,
  sessionActivityKey,
  sessionRecordKey
} from './sessionTermination.helpers';
import type {
  SessionRecordStorage,
  SessionTerminationRecord,
  SessionTerminationState
} from './sessionTermination.types';

// ─── Constants and types ────────────────────────────

/**
 * Index of the records this store owns.
 *
 * Kept explicitly rather than scanning localStorage, because the store is
 * given an injected storage interface with no way to enumerate keys.
 */
const SESSION_RECORD_INDEX_KEY = `${SESSION_RECORD_PREFIX}index`;

/** What eviction needs to know about a record, without reading them all. */
interface SessionRecordIndexEntry {
  key: string;
  state: SessionTerminationState;
  updatedAt: number;
}

const defaultStorage: SessionRecordStorage = {
  read: LocalStorage.read,
  write: LocalStorage.write,
  remove: LocalStorage.remove
};

/**
 * Turns one stored element into a typed entry, so the rest of this file works
 * with `SessionRecordIndexEntry` instead of raw storage output.
 */
function toIndexEntry(value: unknown): SessionRecordIndexEntry | undefined {
  const key = property(value, 'key');
  const state = property(value, 'state');
  const updatedAt = property(value, 'updatedAt');

  if (typeof key !== 'string' || typeof updatedAt !== 'number') {
    return undefined;
  }

  if (state !== 'closing' && state !== 'closed' && state !== 'uncertain') {
    return undefined;
  }

  return { key, state, updatedAt };
}

// ─── Helpers ────────────────────────────────────────

/** Lifetime of one record, measured from `updatedAt`. */
function recordLifetime(state: SessionTerminationState): number {
  return state === 'closing'
    ? CLOSING_SESSION_RECORD_TTL_MS
    : FINISHED_SESSION_RECORD_TTL_MS;
}

/**
 * Reads the session id back out of a record key.
 *
 * Activity stamps use that id under a different prefix, so cleanup can remove
 * both when the close record itself is no longer needed.
 */
function sessionIdFromRecordKey(key: string): string | undefined {
  if (!key.startsWith(SESSION_RECORD_PREFIX)) return undefined;

  const encodedId = key.slice(SESSION_RECORD_PREFIX.length);

  if (!encodedId || encodedId === 'index') return undefined;

  try {
    return decodeURIComponent(encodedId);
  } catch {
    return undefined;
  }
}

// ─── Class ──────────────────────────────────────────

/**
 * Keeps one close record per backend session id.
 *
 * This is what stops a second tab repeating a `CloseSession` that has already
 * been sent, and what lets a tab that missed the live notice discover the
 * close after the fact.
 *
 * Records expire from `updatedAt`: an abandoned `closing` claim after the idle
 * window, and a finished or uncertain close after one hour. Expiration is
 * applied on read and write. The 128-record cap remains for a burst that
 * arrives inside that hour.
 */
export class SessionTerminationRecordStore {
  constructor(
    private readonly storage: SessionRecordStorage = defaultStorage,
    private readonly now: () => number = Date.now
  ) {}

  /**
   * @returns the stored record, or `undefined` when no close has been started.
   * @throws when a record exists but is unreadable — the caller must not read
   * that as "no close has happened".
   */
  read(sessionId: string): SessionTerminationRecord | undefined {
    const key = sessionRecordKey(sessionId);

    this.pruneExpired(this.readIndex());

    const value = this.storage.read(key);

    if (value === null || value === undefined) return undefined;

    if (!isSessionTerminationRecord(value) || value.sessionId !== sessionId)
      throw new Error('Invalid session termination record');

    if (this.isExpired(value.state, value.updatedAt)) {
      this.storage.remove(key);
      this.removeActivity(value.state, sessionId);

      return undefined;
    }

    return value;
  }

  /** Saves a new record, or advances an existing one to its final state. */
  write(record: SessionTerminationRecord): void {
    const key = sessionRecordKey(record.sessionId);
    let index = this.pruneExpired(this.readIndex()).filter(
      (entry) => entry.key !== key
    );
    const existing = this.storage.read(key);

    if (
      (existing === null || existing === undefined) &&
      index.length >= SESSION_RECORD_LIMIT
    ) {
      index = this.evictOldestFinished(index);
    }

    this.storage.write(key, record);
    this.removeActivity(record.state, record.sessionId);

    index.push({
      key,
      state: record.state,
      updatedAt: record.updatedAt
    });
    this.storage.write(SESSION_RECORD_INDEX_KEY, index);
  }

  private readIndex(): SessionRecordIndexEntry[] {
    const value = this.storage.read(SESSION_RECORD_INDEX_KEY);

    if (!Array.isArray(value)) return [];

    return value.flatMap((entry) => toIndexEntry(entry) ?? []);
  }

  /**
   * Frees a slot at capacity, preferring to forget the oldest finished close.
   *
   * `closing` and `uncertain` records are never evicted: they are the only
   * thing preventing a duplicate close. When nothing is safe to drop the index
   * simply grows, because failing here would block a logout.
   */
  private evictOldestFinished(
    index: SessionRecordIndexEntry[]
  ): SessionRecordIndexEntry[] {
    const oldest = index
      .filter((entry) => entry.state === 'closed')
      .sort((left, right) => left.updatedAt - right.updatedAt)[0];

    if (!oldest) return index;

    this.storage.remove(oldest.key);
    this.removeActivity(oldest.state, sessionIdFromRecordKey(oldest.key));

    return index.filter((entry) => entry.key !== oldest.key);
  }

  /** Drops records whose age exceeds the lifetime of their state. */
  private pruneExpired(
    index: SessionRecordIndexEntry[]
  ): SessionRecordIndexEntry[] {
    const remaining: SessionRecordIndexEntry[] = [];
    let removed = false;

    index.forEach((entry) => {
      if (!this.isExpired(entry.state, entry.updatedAt)) {
        remaining.push(entry);
        return;
      }

      removed = true;
      this.storage.remove(entry.key);
      this.removeActivity(entry.state, sessionIdFromRecordKey(entry.key));
    });

    if (removed) {
      this.storage.write(SESSION_RECORD_INDEX_KEY, remaining);
    }

    return remaining;
  }

  private isExpired(
    state: SessionTerminationState,
    updatedAt: number
  ): boolean {
    return this.now() - updatedAt > recordLifetime(state);
  }

  /**
   * A finished close no longer needs the Angular activity stamp. Removing it
   * here keeps a dead session from looking active to Angular's idle poll.
   */
  private removeActivity(
    state: SessionTerminationState,
    sessionId: string | undefined
  ): void {
    if (state !== 'closed' || !sessionId) return;

    this.storage.remove(sessionActivityKey(sessionId));
  }
}
